import { useRef, useState } from 'react';

interface AccordionItem {
  q: string;
  a: string;
}

interface AccordionProps {
  items: AccordionItem[];
}

export default function Accordion({ items }: AccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const toggle = (i: number) => {
    const button = buttonRefs.current[i];
    const beforeTop = button?.getBoundingClientRect().top ?? 0;

    setOpenIndex(openIndex === i ? null : i);

    requestAnimationFrame(() => {
      const afterTop = button?.getBoundingClientRect().top ?? beforeTop;
      const delta = afterTop - beforeTop;

      // Keep the clicked question anchored in viewport to prevent jumpy feeling.
      if (Math.abs(delta) > 1) {
        window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      }
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            className={`rounded-xl border transition-all duration-300 overflow-hidden ${
              isOpen ? 'border-brand-orange/30 bg-brand-orange/5' : 'border-gray-100 bg-white hover:border-gray-200'
            }`}
          >
            <button
              ref={(el) => {
                buttonRefs.current[i] = el;
              }}
              onClick={() => toggle(i)}
              type="button"
              className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
            >
              <span className={`font-heading font-semibold text-sm md:text-base transition-colors duration-200 ${isOpen ? 'text-brand-orange' : 'text-brand-navy'}`}>
                {item.q}
              </span>
              <span className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                isOpen ? 'bg-brand-orange text-white rotate-45' : 'bg-gray-100 text-gray-400'
              }`}>
                <i className="ri-add-line text-lg"></i>
              </span>
            </button>

            <div
              style={{
                maxHeight: isOpen ? '400px' : '0px',
                transition: 'max-height 0.35s ease',
                overflow: 'hidden',
              }}
            >
              <div className="px-6 pb-5">
                <div className="w-full h-px bg-brand-orange/10 mb-4"></div>
                <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
